import { useState } from "react";
import { useT } from "../i18n/hooks.ts";
import type { DailySeries } from "../model/dailySeries.ts";
import type { CalculationOptions } from "../model/calculationOptions.ts";
import { Dialog } from "./Dialog.tsx";
import { InfoIcon } from "./InfoIcon.tsx";

/**
 * Calculation options dialog — start/end date (with a calendar picker,
 * validated against the real range of the loaded precipitation, not a free
 * index like in the original) and the 2 aquifer checkboxes. Always opens on
 * pressing "Calculate"; to recalculate without going through here again,
 * see the secondary button in TopBar.tsx (active once it's been confirmed
 * once).
 */
export function CalculationOptionsDialog({
  precipitation,
  initialValues,
  onConfirm,
  onClose,
}: {
  precipitation: DailySeries;
  initialValues: CalculationOptions;
  onConfirm: (options: CalculationOptions) => void;
  onClose: () => void;
}) {
  const t = useT();
  const minDate = precipitation.dates[0] ?? "";
  const maxDate = precipitation.dates[precipitation.dates.length - 1] ?? "";

  const [startDate, setStartDate] = useState(initialValues.startDate);
  const [endDate, setEndDate] = useState(initialValues.endDate);
  const [negativeAquifers, setNegativeAquifers] = useState(initialValues.negativeAquifers);
  const [modelPumping, setModelPumping] = useState(initialValues.modelPumping);

  const error =
    !minDate || !maxDate
      ? t("calculationOptions.errorNoDates")
      : startDate < minDate || startDate > maxDate
        ? t("calculationOptions.errorStartDateOutOfRange", { min: minDate, max: maxDate })
        : endDate < minDate || endDate > maxDate
          ? t("calculationOptions.errorEndDateOutOfRange", { min: minDate, max: maxDate })
          : endDate < startDate
            ? t("calculationOptions.errorEndBeforeStart")
            : null;

  return (
    <Dialog title={t("calculationOptions.title")} onClose={onClose}>
      <div className="assigner-controls">
        <label>
          {t("calculationOptions.startDate")}{" "}
          <InfoIcon text={t("calculationOptions.availableRange", { min: minDate, max: maxDate })} />
        </label>
        <input
          type="date"
          min={minDate}
          max={maxDate}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
      </div>
      <div className="assigner-controls">
        <label>{t("calculationOptions.endDate")}</label>
        <input
          type="date"
          min={minDate}
          max={maxDate}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>

      <div className="assigner-controls">
        <label>{t("calculationOptions.negativeAquifers")}</label>
        <input
          type="checkbox"
          checked={negativeAquifers}
          onChange={(e) => setNegativeAquifers(e.target.checked)}
        />
      </div>
      <div className="assigner-controls">
        <label>{t("calculationOptions.modelPumping")}</label>
        <input
          type="checkbox"
          checked={modelPumping}
          onChange={(e) => setModelPumping(e.target.checked)}
        />
      </div>

      {error && <p className="error">{error}</p>}

      <div className="dialog-actions">
        <button
          className="button button-primary"
          disabled={!!error}
          onClick={() =>
            onConfirm({
              startDate,
              endDate,
              negativeAquifers,
              modelPumping,
            })
          }
        >
          {t("topbar.calculate")}
        </button>
        <button className="button" onClick={onClose}>
          {t("common.cancel")}
        </button>
      </div>
    </Dialog>
  );
}
